// ═══════════════════════════════════════════════════════════════════════════
// Regelwerks-Prüfung für KI-Antworten — der reine Teil (ohne Netz, ohne Deno)
//
// Der gemini-proxy schickt jede Antwort einer ERZEUGENDEN Aktion hier durch,
// bevor sie die Funktion verlässt. Grundsatz: in keinem Dokument, das die App
// erstellt, darf eine ausgedachte Vorschrift stehen. Ein Satz im Prompt ist
// dafür keine Sicherung — das Modell hat wiederholt Paragraphen und ganze
// Regelwerke erfunden. Deshalb drei Schritte NACH der Antwort:
//
//   1. Paragraphen-, Absatz-, Satz- und Nummernangaben fallen weg, in
//      Regelwerksfeldern wie im Fließtext („nach § 12 DGUV V 38" → „nach
//      DGUV V 38"). Eine geratene Fundstelle ist schlimmer als keine.
//   2. Jede genannte Vorschrift wird gegen SICHERE_REGELWERKE geprüft. Was
//      dort nicht steht, prüft der Proxy online (Google-Suche, nur amtliche
//      Quellen zählen, siehe AMTLICHE_DOMAINS) und merkt sich das Ergebnis
//      in `public.regelwerk_pruefungen`.
//   3. Was weder hier noch online bestätigt ist, wird entfernt — aus dem
//      Feld ebenso wie aus dem Fließtext — und dem Nutzer gemeldet.
//
// Bewusst NICHT geprüft werden die Aktionen, die nur aus einem Dokument
// LESEN (LV, Rechnung, Altbericht …): dort ist ein Paragraph Inhalt des
// Belegs, kein Vorschlag der KI. Die Liste steht im Proxy.
//
// Gemeinsam genutzt von supabase/functions/gemini-proxy/index.ts und den
// Tests unter src/services/regelwerkPruefung.test.ts.
// ═══════════════════════════════════════════════════════════════════════════

/** Schlüssel (kleingeschrieben), deren Wert eine Regelwerksangabe ist. */
export const REGELWERK_FELDER: ReadonlySet<string> = new Set([
  'regulation',
  'regulations',
  'regelwerkreferenz',
  'vorschrift',
  'vorschriften',
  'regelwerk',
  'regelwerke',
  'rechtsgrundlage',
  'rechtsgrundlagen',
  'gesetz',
]);

/**
 * Schlüssel, deren Wert nie verändert wird: Kennungen und Namen, die das
 * Modell laut Prompt WORTGETREU aus dem Kontext übernehmen muss. Ein
 * veränderter Gewerkename fände seinen Partner im Client nicht mehr.
 */
const UNVERAENDERT: ReadonlySet<string> = new Set([
  'id', 'kapitelids', 'tourstart', 'kapitel', 'a', 'b', 'gewerka', 'gewerkb',
  'arbeitsschritt', 'box', 'severity', 'prioritaet', 'schweregrad', 'kategorie',
  'raumbezug', 'ergebnis', 'quelle',
]);

/**
 * Vorschriften, die ohne Online-Abfrage als bestätigt gelten.
 *
 * Bewusst KLEIN gehalten: nur Bundesgesetze und -verordnungen des
 * Arbeitsschutzes sowie die zentralen DGUV-Vorschriften, RAB, ASR, TRBS und
 * TRGS der Baustelle, die in der App ständig vorkommen. Alles andere geht in
 * die Online-Prüfung und landet danach im Speicher — eine Liste aus dem
 * Gedächtnis wäre genau das Raten, das hier verhindert werden soll.
 *
 * Wer einen Eintrag ergänzt, belegt ihn an der amtlichen Quelle
 * (gesetze-im-internet.de, publikationen.dguv.de, baua.de).
 */
export const SICHERE_REGELWERKE: readonly string[] = Object.freeze([
  // Gesetze und Verordnungen (gesetze-im-internet.de)
  'ArbSchG', 'ASiG', 'ArbZG', 'JArbSchG', 'MuSchG', 'SGB VII', 'ProdSG', 'ChemG',
  'ArbStättV', 'BaustellV', 'BetrSichV', 'GefStoffV', 'BioStoffV',
  'LärmVibrationsArbSchV', 'PSA-BV', 'ArbMedVV', 'OStrV', 'EMFV',
  'StVO', 'WHG', 'KrWG', 'BImSchG', 'BBodSchG', 'BBodSchV',
  // Unfallverhütungsvorschriften (publikationen.dguv.de)
  'DGUV Vorschrift 1', 'DGUV Vorschrift 2', 'DGUV Vorschrift 3',
  'DGUV Vorschrift 38', 'DGUV Vorschrift 52',
  'DGUV Regel 100-001', 'DGUV Regel 100-500', 'DGUV Regel 101-004',
  // Regeln zum Arbeitsschutz auf Baustellen (baua.de)
  'RAB 10', 'RAB 30', 'RAB 31', 'RAB 32', 'RAB 33',
  // Technische Regeln für Arbeitsstätten (baua.de)
  'ASR A1.3', 'ASR A1.8', 'ASR A2.1', 'ASR A2.2', 'ASR A2.3',
  'ASR A3.4', 'ASR A4.1', 'ASR A4.3', 'ASR A5.2',
  // Technische Regeln für Betriebssicherheit / Gefahrstoffe (baua.de)
  'TRBS 1201', 'TRBS 1203', 'TRBS 2121', 'TRBS 2121 Teil 1', 'TRBS 2121 Teil 2',
  'TRBS 2121 Teil 3',
  'TRGS 519', 'TRGS 521', 'TRGS 524', 'TRGS 528', 'TRGS 559',
  // Verkehrsrechtliche Sicherung von Arbeitsstellen an Straßen, ersetzt die
  // RSA 95 (ARS 24/2021, Verkehrsblatt Heft 3/2022). Ihre amtlichen
  // Fundstellen (Verkehrsblatt, Landesportale) stehen nicht in
  // AMTLICHE_DOMAINS — online würde sie deshalb nie bestätigt (Betreiber
  // 18.10.2026). „RSA" ohne Fassung bleibt mehrdeutig und wird weiter geprüft.
  'RSA 21',
]);

/**
 * Domains, deren Fundstelle eine Vorschrift online bestätigt. Treffer zählen
 * auch für Unterdomains (publikationen.dguv.de → dguv.de). Fachportale,
 * Händler und Blogs zählen NICHT — sie schreiben voneinander ab, auch
 * Fehler.
 */
export const AMTLICHE_DOMAINS: readonly string[] = Object.freeze([
  'gesetze-im-internet.de', 'recht.bund.de', 'bmas.de',
  'dguv.de', 'baua.de',
  'bgbau.de', 'bghm.de', 'bgrci.de', 'bgetem.de', 'bg-verkehr.de', 'bgn.de',
  'bgw-online.de', 'vbg.de', 'svlfg.de',
  'dinmedia.de', 'din.de', 'beuth.de', 'vde.com', 'vde-verlag.de',
  'eur-lex.europa.eu',
  // Englische HSE-Berichte nennen ISO-Normen und OSHA
  'iso.org', 'osha.gov', 'ecfr.gov', 'hse.gov.uk',
]);

/** Höchstens so viele Vorschriften je Antwort gehen in die Online-Prüfung. */
export const MAX_ONLINE_PRUEFUNGEN = 12;

/**
 * Die Regel, die der Proxy JEDER erzeugenden Aktion als `systemInstruction`
 * mitgibt — auch den Prompts, die der Client selbst baut. Sie senkt die
 * Zahl der Erfindungen; die Sicherung ist die Nachprüfung oben.
 */
export const REGELWERK_SYSTEM = [
  'Für jede Antwort gilt ohne Ausnahme:',
  '1. Nenne nur Regelwerke (Gesetze, Verordnungen, DGUV-Vorschriften, -Regeln und -Informationen, TRBS, TRGS, ASR, RAB, DIN-, EN- und ISO-Normen), die tatsächlich existieren und derzeit gültig sind.',
  '2. Nenne KEINE Paragraphen-, Absatz-, Satz-, Nummern- oder Anhangsangaben — weder in Regelwerksfeldern noch im Fließtext. Nenne nur das Regelwerk selbst (z. B. "DGUV Vorschrift 38", "BetrSichV").',
  '3. Bist du nicht sicher, dass ein Regelwerk existiert und passt, lass es weg. Ein leeres Regelwerksfeld ist eine gültige Antwort.',
  '4. Jede Regelwerksangabe wird nach deiner Antwort online gegen amtliche Quellen geprüft; was sich nicht bestätigen lässt, wird entfernt.',
].join('\n');

// ─── Paragraphen ────────────────────────────────────────────────────────────

/**
 * Eine Stelle hinter dem Paragraphenzeichen: „9", „12a", „9 (2)", „4 Abs. 2
 * Satz 1 Nr. 3". Der Buchstabe hinter der Nummer zählt nur direkt angehängt
 * („12a"), sonst fräße das Muster den Anfang des nächsten Wortes („§ 4 des …").
 */
const STELLE =
  String.raw`\d+[a-z]?(?![a-zäöüß])(?:\s*\(\d+\))?(?:\s*(?:Abs\.?|Absatz)\s*\d+[a-z]?(?![a-zäöüß]))?(?:\s*(?:S\.|Satz)\s*\d+)?(?:\s*(?:Nr\.?|Nummer)\s*\d+[a-z]?(?![a-zäöüß]))?`;

/**
 * Eine Fundstelle mit Paragraph. Ohne Paragraphenzeichen wird nichts
 * entfernt — eine Zahl allein ist im Fließtext meist eine Menge oder ein Maß.
 * Eine AUFZÄHLUNG („und 4", „, 5") gehört nur zum Doppelzeichen „§§": hinter
 * einem einfachen „§ 4 und 2 Mitarbeiter …" ist die 2 eine Menge (Codex-Review
 * 13.10.).
 */
const PARAGRAPH = new RegExp(
  String.raw`\s*(?:§§\s*${STELLE}(?:\s*(?:,|und|bis)\s*${STELLE})*|§\s*${STELLE})`,
  'g',
);

/** Bindewörter, die nach dem Entfernen ohne Bezug stehen blieben. */
const VERWAISTES_BINDEWORT =
  /\s*\b(?:gemäß|gem\.|nach|laut|lt\.|siehe|vgl\.|entsprechend|i\.\s?S\.\s?d\.|i\.\s?V\.\s?m\.|in Verbindung mit)(?=\s*(?:[.,;)]|$))/gi;

/** Fließtext: Paragraphenangaben heraus, Satzzeichen danach aufräumen. */
export const ohneParagraphen = (text: string): string => {
  if (!text.includes('§')) return text;
  return aufraeumen(text.replace(PARAGRAPH, ''));
};

const aufraeumen = (text: string): string =>
  text
    .replace(VERWAISTES_BINDEWORT, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/([,;])\s*(?=[,;.])/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();

// ─── Regelwerksfelder ───────────────────────────────────────────────────────

/**
 * Ein Regelwerksfeld in einzelne Angaben zerlegen. Getrennt wird an Komma,
 * Semikolon, Mittelpunkt, Zeilenumbruch und den Bindewörtern „und",
 * „sowie", „i. V. m." — NICHT am Schrägstrich („DIN EN 12810/12811" ist eine
 * Angabe).
 */
export const zerlegeRegelwerke = (wert: string): string[] =>
  wert
    .split(/[,;·\n]|\s+(?:und|sowie|bzw\.|i\.\s?V\.\s?m\.|in Verbindung mit)\s+/i)
    .map(teil => kernAngabe(teil))
    .filter(Boolean);

/** Titel in Anführungszeichen und Zusätze in Klammern. */
const ZUSATZ = /[„"“”‚‘'][^„"“”‚‘']*[“”"‘’']|\([^)]*\)?/g;

/**
 * Die Angabe, wie sie geprüft UND ausgegeben wird: ohne Paragraph und ohne
 * Zusatz in Klammern oder Anführungszeichen. Geprüft wird nur der Kern —
 * deshalb darf auch nur der Kern im Dokument stehen. Sonst rutschte
 * „ArbSchG (PhantasieSchutzV)" mit dem Schlüssel von „ArbSchG" durch
 * (Codex-Review 13.10.).
 */
export const kernAngabe = (angabe: string): string =>
  bereinigeAngabe(bereinigeAngabe(angabe).replace(ZUSATZ, ' '));

/**
 * Eine einzelne Angabe auf das Regelwerk kürzen: Paragraph, Absatz, Nummer
 * und Anhang fallen weg („Anhang II Nr. 1 BaustellV" → „BaustellV").
 */
export const bereinigeAngabe = (angabe: string): string =>
  angabe
    .replace(PARAGRAPH, ' ')
    .replace(/\b(?:Anh\.?|Anhang)\s+[0-9IVXivx]+(?:\s*(?:Nr\.?|Nummer)\s*\d+)?/g, ' ')
    .replace(/\b(?:Abs\.?|Absatz|Satz|Nr\.?|Nummer|Abschn\.?|Abschnitt)\s*\d+(?:\.\d+)*/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s\-–,.:]+|[\s\-–,.:]+$/g, '')
    .trim();

/**
 * Vergleichsschlüssel einer Angabe: Schreibvarianten derselben Vorschrift
 * ergeben denselben Schlüssel („DGUV Vorschrift 38" = „DGUV V 38" =
 * „DGUV-Vorschrift 38"; „TRBS 2121 Teil 1" = „TRBS 2121-1"; „ASR A 2.1" =
 * „ASR A2.1"). Titel in Anführungszeichen oder Klammern zählen nicht.
 */
export const regelwerkSchluessel = (angabe: string): string =>
  vereinheitliche(kernAngabe(angabe))
    .replace(/\s+/g, '')
    .replace(/[.,;:]+$/, '');

/** Schreibweisen angleichen, Leerzeichen bleiben (für den Wortvergleich). */
const vereinheitliche = (text: string): string =>
  text
    .toLowerCase()
    .replace(/dguv[\s-]*vorschrift\b/g, 'dguv v')
    .replace(/dguv[\s-]*regel\b/g, 'dguv r')
    .replace(/dguv[\s-]*information\b/g, 'dguv i')
    .replace(/dguv[\s-]*grundsatz\b/g, 'dguv g')
    .replace(/\s*(?:teil|t\.)\s*(\d)/g, '-$1')
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Nennt `text` genau diese Vorschrift? Nummerierte Kennungen werden ohne
 * Leerzeichen verglichen und dürfen nicht in eine längere Nummer übergehen
 * („DGUV Regel 112-19" steckt NICHT in „DGUV Regel 112-198"). Kennungen ohne
 * Ziffer (Gesetzeskürzel) müssen als ganzes Wort stehen („SchG" steckt nicht
 * in „ArbSchG").
 */
export const nenntKennung = (text: string, angabe: string): boolean => {
  const zeichen = vereinheitliche(kernAngabe(angabe)).replace(/\s+/g, '');
  if (!zeichen) return false;
  // Leerzeichen innerhalb der Kennung sind frei („ASR A 2.1" = „ASR A2.1"),
  // die Grenzen werden aber im Text MIT Leerzeichen geprüft: „XTRGS 999"
  // nennt nicht „TRGS 999", „TRGS 999 2. Ausgabe" dagegen schon
  // (Codex-Review 13.10., Runde 2).
  const muster = [...zeichen].map(z => z.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')).join('\\s*');
  // Dahinter darf die AUSGABE stehen („…:2008-12") — bei Normen auch der
  // TEIL („DIN EN 12811-1", „DIN VDE 0100-100"): so nennt Google sie, und
  // die Reihe existiert, wenn ihr Teil existiert. Eine längere NUMMER zählt
  // nie („DGUV Regel 112-19" ist nicht „112-198"), und außerhalb der Normen
  // auch kein Bindestrich-Anhang („DGUV Regel 112" ist nicht „112-198").
  // Vorher lehnte die Prüfung jeden Anhang ab und verwarf echte Normen
  // (DIN EN 12811/12812, DIN VDE 0100 — Betreiber 13.10.).
  const istNorm = /^(?:din|en|iso|iec|vde)/.test(zeichen);
  const ende = istNorm ? String.raw`(?![a-zäöüß0-9]|[./][0-9])` : String.raw`(?![a-zäöüß0-9]|[-./][0-9])`;
  return new RegExp(`(?<![a-zäöüß0-9])${muster}${ende}`).test(vereinheitliche(text));
};

const SICHERE_SCHLUESSEL: ReadonlySet<string> = new Set(SICHERE_REGELWERKE.map(regelwerkSchluessel));

/** Steht der Schlüssel (regelwerkSchluessel) in der festen Liste? */
export const istSichererSchluessel = (schluessel: string): boolean => SICHERE_SCHLUESSEL.has(schluessel);

/** Steht die Angabe in der festen Liste? */
export const istSicheresRegelwerk = (angabe: string): boolean =>
  istSichererSchluessel(regelwerkSchluessel(angabe));

// ─── Nennungen im Fließtext ─────────────────────────────────────────────────

/**
 * Nummerierte Regelwerke im Fließtext. Gesetzeskürzel ohne Nummer erkennt
 * dieses Muster nicht — sie lassen sich von Abkürzungen wie „PSA" oder „LKW"
 * nicht sicher trennen. Dort wirken Systemregel und Prompt; in den
 * Regelwerksfeldern wird dagegen JEDE Angabe geprüft.
 */
/**
 * Die VOLLE Nummer samt Anhängseln („112-999", „2.1", „12810/12811",
 * „9001:2015"). Würde nur der Anfang erfasst, prüfte die Kette „TRGS 519"
 * (bekannt) und ließe „TRGS 519-999" (erfunden) stehen (Codex-Review 13.10.).
 */
const NUMMER = String.raw`\d+(?:[-./:]\d+)*`;

/** „Teil 1", „T. 2-3" hinter der Nummer — ebenfalls mit voller Nummer. */
const TEIL = String.raw`(?:\s*(?:Teil|T\.)\s*${NUMMER})?`;

const NENNUNG = new RegExp(
  [
    String.raw`DGUV[\s-]*(?:Vorschrift|Regel|Information|Grundsatz|[VRIG])\s*${NUMMER}`,
    String.raw`TRBS\s*${NUMMER}${TEIL}`,
    String.raw`TRGS\s*${NUMMER}${TEIL}`,
    String.raw`ASR\s*[AV]\s?${NUMMER}(?:,\d+)*`,
    String.raw`(?:DIN|EN|ISO|IEC)(?:\s+(?:EN|ISO|IEC))*\s*${NUMMER}${TEIL}`,
    String.raw`RAB\s*${NUMMER}`,
    String.raw`VDE\s*${NUMMER}${TEIL}`,
  ].map(m => `\\b${m}(?![0-9]|[-./:][0-9])`).join('|'),
  'g',
);

/** Alle nummerierten Regelwerke, die im Text genannt sind. */
export const findeNennungen = (text: string): string[] =>
  [...text.matchAll(NENNUNG)].map(m => m[0].replace(/\s+/g, ' ').trim());

/**
 * Eine nicht bestätigte Nennung samt einleitendem Bindewort aus dem Text
 * nehmen: „Seitenschutz nach DGUV Regel 112-999 anbringen" → „Seitenschutz
 * anbringen".
 */
const entferneNennung = (text: string, nennung: string): string => {
  const muster = nennung.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&').replace(/\s+/g, '\\s*');
  return aufraeumen(text.replace(
    // Dahinter darf ein Satzzeichen stehen („… 112-999."), aber keine
    // Fortsetzung der Nummer („… 112-9990", „… 112-999-1").
    new RegExp(String.raw`\s*(?:\b(?:gemäß|gem\.|nach|laut|lt\.|siehe|vgl\.|entsprechend|i\.\s?S\.\s?d\.|i\.\s?V\.\s?m\.)\s*)?${muster}(?![0-9]|[-./:][0-9])`, 'g'),
    '',
  ));
};

// ─── Durchlauf über die Antwort ─────────────────────────────────────────────

const istObjekt = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Alle Angaben einer Antwort, die geprüft werden müssen — aus den
 * Regelwerksfeldern jede Angabe, aus dem Fließtext die nummerierten
 * Nennungen. Ergebnis eindeutig je Schlüssel, in der Reihenfolge des ersten
 * Auftretens.
 */
export const sammleRegelwerke = (daten: unknown): string[] => {
  const gefunden = new Map<string, string>();
  const merke = (angabe: string) => {
    const schluessel = regelwerkSchluessel(angabe);
    if (schluessel && !gefunden.has(schluessel)) gefunden.set(schluessel, angabe);
  };
  const lauf = (wert: unknown, feld: string | null): void => {
    if (typeof wert === 'string') {
      if (feld && REGELWERK_FELDER.has(feld)) zerlegeRegelwerke(wert).forEach(merke);
      else findeNennungen(wert).forEach(merke);
    } else if (Array.isArray(wert)) {
      wert.forEach(e => lauf(e, feld));
    } else if (istObjekt(wert)) {
      const imRegelwerk = !!feld && REGELWERK_FELDER.has(feld);
      for (const [k, v] of Object.entries(wert)) {
        const klein = k.toLowerCase();
        if (k.startsWith('_') || (!imRegelwerk && UNVERAENDERT.has(klein))) continue;
        lauf(v, imRegelwerk ? feld : klein);
      }
    }
  };
  lauf(daten, null);
  return [...gefunden.values()];
};

/** Ein Schritt im Pfad durch die Antwort: Feldname oder Listenposition. */
export type PfadSchritt = string | number;

/**
 * WO eine Vorschrift entfernt wurde. Der Client zeigt damit, aus welchem
 * Eintrag sie stammt, und kann sie nach Bestätigung durch den Nutzer genau
 * dorthin zurücklegen (Betreiber 13.10.: „man weiß nicht, aus welchem Eintrag
 * das stammt").
 *
 * - `art: 'feld'` — aus einem Regelwerksfeld; `pfad` zeigt auf das FELD
 *   (Text oder Liste), nicht auf das einzelne Listenelement.
 * - `art: 'text'` — aus einem Fließtext; `pfad` zeigt auf den Text.
 * - `pfad: null` — nicht zuordenbar (abgeschnittene Antwort, die als Text
 *   weitergeht).
 */
export interface VerworfeneStelle {
  angabe: string;
  pfad: PfadSchritt[] | null;
  art: 'feld' | 'text';
}

export interface Bereinigung {
  daten: unknown;
  /** Angaben, die entfernt wurden (je Schlüssel einmal). */
  verworfen: string[];
  /** Jede Stelle, an der etwas entfernt wurde — für den Dialog im Client. */
  stellen: VerworfeneStelle[];
}

/**
 * Antwort bereinigen. `bestaetigt` bekommt den Schlüssel (regelwerkSchluessel)
 * und sagt, ob die Vorschrift fest bekannt oder online bestätigt ist.
 *
 * - Regelwerksfeld (Text): nur bestätigte Angaben bleiben, mit „, " verbunden.
 * - Regelwerksfeld (Liste): nur bestätigte Einträge bleiben.
 * - Fließtext: Paragraphen fallen weg, nicht bestätigte Nennungen ebenso.
 *
 * Die Form der Antwort bleibt erhalten (kein Feld verschwindet, aus einem
 * Text wird kein Array) — die Aufrufer im Client lesen sie unverändert.
 */
export const bereinigeRegelwerke = (
  daten: unknown,
  bestaetigt: (schluessel: string) => boolean,
): Bereinigung => {
  const verworfen = new Map<string, string>();
  const stellen: VerworfeneStelle[] = [];
  const gemeldet = new Set<string>();
  const behalte = (angabe: string, pfad: PfadSchritt[], art: VerworfeneStelle['art']): boolean => {
    const schluessel = regelwerkSchluessel(angabe);
    if (bestaetigt(schluessel)) return true;
    if (!verworfen.has(schluessel)) verworfen.set(schluessel, angabe);
    const kennung = `${schluessel}|${JSON.stringify(pfad)}`;
    if (!gemeldet.has(kennung)) {
      gemeldet.add(kennung);
      stellen.push({ angabe, pfad: [...pfad], art });
    }
    return false;
  };

  const fliesstext = (text: string, pfad: PfadSchritt[]): string => {
    let neu = ohneParagraphen(text);
    for (const nennung of findeNennungen(neu)) {
      if (!behalte(nennung, pfad, 'text')) neu = entferneNennung(neu, nennung);
    }
    return neu;
  };

  // `wurzel`: Pfad des Regelwerksfelds, in dem der Lauf gerade steckt (null
  // außerhalb). Verschachteltes darin wird dem Feld selbst zugeordnet.
  const lauf = (wert: unknown, feld: string | null, pfad: PfadSchritt[], wurzel: PfadSchritt[] | null): unknown => {
    const istRegelwerksfeld = !!feld && REGELWERK_FELDER.has(feld);
    const feldPfad = wurzel ?? (istRegelwerksfeld ? pfad : null);
    if (typeof wert === 'string') {
      return feldPfad
        ? zerlegeRegelwerke(wert).filter(a => behalte(a, feldPfad, 'feld')).join(', ')
        : fliesstext(wert, pfad);
    }
    if (Array.isArray(wert)) {
      if (feldPfad) {
        // Verschachtelte Listen bleiben Regelwerksfeld — sonst liefe
        // [["PhantasieSchutzV"]] als Fließtext ungeprüft durch.
        return wert.flatMap((e, i) => typeof e === 'string'
          ? zerlegeRegelwerke(e).filter(a => behalte(a, feldPfad, 'feld'))
          : [lauf(e, feld, [...pfad, i], feldPfad)]);
      }
      return wert.map((e, i) => lauf(e, feld, [...pfad, i], null));
    }
    if (istObjekt(wert)) {
      // Ein Objekt INNERHALB eines Regelwerksfelds erbt den Zusammenhang: jeder
      // Wert darin ist eine Regelwerksangabe ({"name": "PhantasieSchutzV"}
      // liefe sonst als Fließtext durch, Codex-Review 13.10., Runde 3).
      const neu: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(wert)) {
        const klein = k.toLowerCase();
        neu[k] = k.startsWith('_') || (!feldPfad && UNVERAENDERT.has(klein))
          ? v
          : lauf(v, feldPfad ? feld : klein, [...pfad, k], feldPfad);
      }
      return neu;
    }
    return wert;
  };

  const ergebnis = lauf(daten, null, [], null);
  return { daten: ergebnis, verworfen: [...verworfen.values()], stellen };
};

// ─── Unlesbare (abgeschnittene) Antworten ───────────────────────────────────
//
// Lässt sich die Antwort nicht als JSON lesen — meist, weil das Modell am
// Token-Limit mitten im JSON aufhört —, gibt der Proxy `{ text }` weiter, und
// der Client rettet daraus die vollständigen Einträge (aiJsonSalvage). Eine
// Prüfung des ROHTEXTS mit Mustern ist dafür nicht dicht: ein escapter
// Feldname (`"regulations"`) oder ein gemischtes Array (`[null, "…"]`)
// liefen vorbei (Codex-Review 13.10., zwei Runden). Deshalb wird der Rohtext
// zuerst zu gültigem JSON REPARIERT — alles bis zum letzten vollständigen
// Element, offene Klammern geschlossen —, dann geparst und wie jede andere
// Antwort geprüft. Der Client bekommt gültiges, geprüftes JSON im Feld
// `text`; sein Rettungsweg liest es unverändert.

/** Höchstens so viele Schnittstellen werden von hinten probiert. */
const MAX_REPARATUR_VERSUCHE = 200;

/**
 * Tiefer verschachtelt antwortet keine Aktion. Darüber wird nicht repariert —
 * jede gemerkte Schnittstelle trägt ihren Klammerstapel, und ohne Grenze
 * wüchse der Speicher mit Tiefe × Schnittstellen (Codex-Review 13.10.).
 */
const MAX_TIEFE = 64;

/**
 * Abgeschnittenes JSON reparieren: an der letzten Stelle abschneiden, an der
 * ein Element vollständig war (vor einem Komma oder hinter einer schließenden
 * Klammer), und die dort offenen Klammern schließen. `undefined`, wenn keine
 * Schnittstelle zu gültigem JSON führt.
 */
export const repariereJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch { /* reparieren */ }

  // Nur die letzten MAX_REPARATUR_VERSUCHE Schnittstellen bleiben; der
  // Stapel wird als kurze Zeichenkette gemerkt („{[["), nicht als Kopie.
  let schnitte: { bis: number; offen: string }[] = [];
  let stapel = '';
  let inZeichenkette = false;
  let maskiert = false;
  const merke = (bis: number) => {
    schnitte.push({ bis, offen: stapel });
    if (schnitte.length > 2 * MAX_REPARATUR_VERSUCHE) schnitte = schnitte.slice(-MAX_REPARATUR_VERSUCHE);
  };
  for (let i = 0; i < text.length; i++) {
    const z = text[i];
    if (inZeichenkette) {
      if (maskiert) maskiert = false;
      else if (z === '\\') maskiert = true;
      else if (z === '"') inZeichenkette = false;
      continue;
    }
    if (z === '"') inZeichenkette = true;
    else if (z === '{' || z === '[') {
      stapel += z;
      if (stapel.length > MAX_TIEFE) return undefined;
    } else if (z === '}' || z === ']') {
      stapel = stapel.slice(0, -1);
      merke(i + 1);
    } else if (z === ',' && stapel.length > 0) {
      merke(i);
    }
  }

  const versuche = schnitte.slice(-MAX_REPARATUR_VERSUCHE).reverse();
  for (const { bis, offen } of versuche) {
    const schluss = [...offen].reverse().map(k => (k === '{' ? '}' : ']')).join('');
    try {
      return JSON.parse(text.slice(0, bis) + schluss);
    } catch { /* nächste Schnittstelle */ }
  }
  return undefined;
};

/**
 * Was die Regelwerks-Prüfung bei einer unlesbaren Antwort prüft: das
 * reparierte JSON, oder — wenn es gar kein JSON sein sollte — den Text als
 * Fließtext. `undefined`: JSON-artig, aber nicht reparierbar; dann darf
 * nichts davon ungeprüft hinaus.
 */
export const prueflingAusRohtext = (rohtext: string): unknown => {
  // JSON kann auch hinter einer Einleitung stehen („Hier das Ergebnis:\n{…}").
  // Dann zählt das JSON ab der ersten Klammer; die Einleitung fällt weg
  // (Codex-Review 13.10., Runde 3). Nur Text ganz ohne Klammer ist Prosa.
  const ohneZaun = rohtext.replace(/```(?:json)?/gi, '').trim();
  const start = ohneZaun.search(/[[{]/);
  if (start < 0) return rohtext;
  return repariereJson(ohneZaun.slice(start));
};

// ─── Online-Prüfung: Auswertung der Google-Antwort ──────────────────────────

/** Gehört die Fundstelle (Titel oder Adresse) zu einer amtlichen Domain? */
export const istAmtlicheQuelle = (fundstelle: { uri?: string; title?: string } | null | undefined): boolean => {
  if (!fundstelle) return false;
  const kandidaten: string[] = [];
  if (typeof fundstelle.title === 'string') kandidaten.push(fundstelle.title.trim().toLowerCase());
  if (typeof fundstelle.uri === 'string') {
    try { kandidaten.push(new URL(fundstelle.uri).hostname.toLowerCase()); } catch { /* keine Adresse */ }
  }
  return kandidaten.some(k => AMTLICHE_DOMAINS.some(d => k === d || k.endsWith(`.${d}`)));
};

/**
 * Prompt der Online-Prüfung für EINE Angabe. Bewusst eine einzige Textzeile
 * statt JSON: die Google-Suche hängt ihre Belege (groundingSupports) an
 * Textabschnitte — an genau dieser Zeile mit der Kennung wird der Beleg
 * gesucht.
 */
export const pruefPrompt = (angabe: string): string =>
  `Prüfe mit der Google-Suche, ob es das folgende Regelwerk tatsächlich gibt und ob es derzeit gültig ist (nicht zurückgezogen, nicht ersetzt): "${angabe}". ` +
  'Stütze dich NUR auf amtliche Quellen (gesetze-im-internet.de, dguv.de, baua.de, Berufsgenossenschaften, DIN Media, VDE, EUR-Lex, ISO, OSHA). ' +
  'Eine NORMENREIHE ohne Teilnummer (z. B. "DIN EN 12811", "DIN VDE 0100") gilt als bestätigt, wenn mindestens ein derzeit gültiger Teil dieser Reihe existiert; nenne dann diesen Teil mit seiner vollständigen Kennung in der Bezeichnung (z. B. "DIN EN 12811-1:2004-03 …"). ' +
  'Findest du es dort nicht, gilt es als nicht bestätigt — rate nicht. ' +
  'Antworte mit GENAU EINER Zeile ohne weiteren Text: ' +
  '"BESTAETIGT: <amtliche Bezeichnung einschließlich der Kennung und des Titels>" — nur wenn du es auf einer amtlichen Seite gefunden hast UND es derzeit gültig ist —, sonst "NICHT BESTAETIGT".';

export type PruefErgebnis = 'bestaetigt' | 'nicht_bestaetigt';

/**
 * Fassung der Online-Prüfung (Prompt + Auswertung). Ein gespeichertes
 * „nicht_bestaetigt" gilt nur für die Fassung, mit der es entstand — ändert
 * sich die Prüfung, wird neu gesucht. Bestätigtes bleibt gültig.
 * Fassung 2 (13.10.): Normenreihen ohne Teilnummer gelten über einen gültigen
 * Teil — „DIN EN 12811" und „DIN VDE 0100" waren mit Fassung 1 verneint worden.
 */
export const PRUEF_FASSUNG = 2;

export interface OnlinePruefung {
  ergebnis: PruefErgebnis;
  bezeichnung: string;
  /** Domain der amtlichen Fundstelle, sonst "". */
  quelle: string;
  /**
   * Darf das Ergebnis gespeichert werden? Nein, wenn das Modell „bestätigt"
   * sagt, der Beleg aber fehlt — das kann an der Suche liegen und soll beim
   * nächsten Mal neu geprüft werden, statt 30 Tage gesperrt zu bleiben.
   */
  endgueltig: boolean;
}

type Fundstelle = { uri?: string; title?: string };

/** Eine Bezeichnung, die selbst sagt, dass die Vorschrift nicht mehr gilt. */
const WIDERRUFEN =
  /zurückgezogen|zurueckgezogen|ersetzt durch|aufgehoben|außer kraft|ausser kraft|nicht mehr gültig|withdrawn|superseded|repealed/i;

/**
 * Antwort der Google-Suche auswerten. Bestätigt ist eine Angabe nur, wenn
 * ALLES zutrifft (Codex-Review 13.10.: „irgendein amtlicher Treffer" reichte
 * nicht — eine erfundene Regel mit einem allgemeinen BAuA-Treffer kam durch):
 *   1. Das Modell antwortet „BESTAETIGT: <Bezeichnung>".
 *   2. Die Bezeichnung nennt GENAU die gefragte Kennung (nenntKennung).
 *   3. Ein Beleg der Suche (groundingSupports) stützt einen Textabschnitt, der
 *      die Kennung nennt, UND verweist dabei auf eine amtliche Fundstelle.
 * Die Fundstellen stammen aus der Suche selbst (groundingMetadata), nicht aus
 * dem Text des Modells — eine erfundene Adresse zählt nicht.
 */
export const werteOnlinePruefungAus = (googleData: any, angabe: string): OnlinePruefung => {
  const kandidat = googleData?.candidates?.[0];
  const roh: string = (kandidat?.content?.parts || [])
    .map((p: { text?: string }) => p?.text || '')
    .join('')
    .trim();
  // Nur eine Umklammerung der GANZEN Zeile entfernen — ein Titel darf auf
  // ein Anführungszeichen enden.
  const text = /^(["'`])[\s\S]*\1$/.test(roh) ? roh.slice(1, -1).trim() : roh;
  // GENAU EINE Zeile: bei mehreren Zeilen könnte ein Beleg für eine andere
  // Zeile („DIN 99999 ist zurückgezogen") die Bestätigung tragen
  // (Codex-Review 13.10., Runde 2). Abweichendes Format zählt nicht.
  const treffer = text.match(/^BEST(?:AE|Ä)TIGT\s*:\s*([^\r\n]+)$/i);
  // Geprüft wird die VOLLE Bezeichnung; gekürzt wird erst für den Speicher —
  // sonst fiele ein „zurückgezogen" hinter Zeichen 300 weg (Codex, Runde 3).
  const volleBezeichnung = treffer ? treffer[1].trim() : '';
  const bezeichnung = volleBezeichnung.slice(0, 300);
  const nein = { ergebnis: 'nicht_bestaetigt' as const, bezeichnung, quelle: '' };

  if (!treffer) return { ...nein, endgueltig: /^NICHT\s+BEST(?:AE|Ä)TIGT\W*$/i.test(text) };
  if (!nenntKennung(volleBezeichnung, angabe) || WIDERRUFEN.test(volleBezeichnung)) return { ...nein, endgueltig: false };

  const fundstellen: (Fundstelle | undefined)[] =
    (kandidat?.groundingMetadata?.groundingChunks || []).map((c: any) => c?.web);
  const belege: any[] = kandidat?.groundingMetadata?.groundingSupports || [];
  for (const beleg of belege) {
    const abschnitt: string = typeof beleg?.segment?.text === 'string' ? beleg.segment.text : '';
    if (!nenntKennung(abschnitt, angabe)) continue;
    const amtlich = (beleg?.groundingChunkIndices || [])
      .map((i: number) => fundstellen[i])
      .find((f: Fundstelle | undefined) => istAmtlicheQuelle(f));
    if (amtlich) {
      return {
        ergebnis: 'bestaetigt',
        bezeichnung,
        quelle: (amtlich.title || amtlich.uri || '').slice(0, 120),
        endgueltig: true,
      };
    }
  }
  return { ...nein, endgueltig: false };
};

// ═══════════════════════════════════════════════════════════════════════════
// Schreibregel: keine Gedankenstriche, Umlaute statt Umschrift
//
// Betreiber 03.11.2026: „in den Fragen Planungsphase, SiGe-Plan,
// Baustellenordnung, A+S-Plan und Mängel niemals Gedankenstriche verwenden.
// Überall Umlaute zulassen.“ Die festen Texte dieser Module sind so
// geschrieben (Wächter: src/services/schreibregel.quelltext.test.ts). Bei der
// KI reicht ein Satz im Prompt nicht, das Modell setzt Gedankenstriche
// trotzdem. Deshalb bekommt jede Aktion dieser Module die Regel als
// Systemanweisung, und ihre Antwort wird danach bereinigt: im Proxy und noch
// einmal in `callAiBackend` (für einen Proxy, der noch nicht neu ausgerollt
// ist). Zweimal angewendet ändert sich nichts mehr.
//
// Umschrift in einer KI-Antwort lässt sich nicht maschinell zurückführen
// („Feuer“, „neue“, „Wasser“ sind richtig geschrieben); dafür steht die Regel
// im Prompt.
//
// Liegt in dieser Datei, weil das Bündel für den Dashboard-Editor genau
// diese eine Datei aus `_shared` einbettet.
// ═══════════════════════════════════════════════════════════════════════════

export const SCHREIBREGEL_SYSTEM = 'Schreibweise für deine eigenen Formulierungen (wörtliche Zitate bleiben unverändert): Verwende keine Gedankenstriche (– oder —) und auch keinen Bindestrich mit Leerzeichen davor und danach als Ersatz. Nutze stattdessen Komma, Doppelpunkt, Klammern oder einen neuen Satz; für Bereiche schreibe „bis“. Schreibe deutsche Umlaute und ß (ä, ö, ü, ß), nie Umschrift wie ae, oe, ue oder ss.';

/**
 * Aktionen der fünf Module → Felder, deren Wert Zeichen für Zeichen bleiben
 * muss (ein Zitat, das der Client gegen das Dokument prüft).
 */
export const SCHREIBREGEL_AKTIONEN: ReadonlyMap<string, readonly string[]> = new Map<string, readonly string[]>([
  // Mängel
  ['analyzeInspectionPhoto', []],
  ['processVoiceTranscript', []],
  ['generateSafetyText', []],
  // SiGe-Plan (analyzeSigePlan liefert auch die Vorschläge der Mängel-Checkliste)
  ['generateSigePlanHazards', []],
  ['analyzeSigePlan', []],
  ['analyzeSigePlanDocument', []],
  ['predictMutualHazards', []],
  ['chatSigePlan', []],
  ['generateWorkSteps', []],
  ['validateSigePlan', []],
  ['assistSigePlan', []],
  // Baustellenordnung
  ['assistBauordnung', []],
  // A+S-Plan
  ['asplanHistorischeErkundung', []],
  // Planungsphase: die Fragen stammen aus dem Katalog; die Einschätzung der
  // Baubeschreibung bringt eine eigene Begründung, ihr Zitat bleibt wörtlich.
  ['einschaetzePlanungBaubeschreibung', ['zitat']],
]);

const GEDANKENSTRICH = /[–—]/;

/**
 * Ein Text ohne Gedankenstriche. Gedacht für KI-Antworten, deren Satzbau
 * niemand mehr nachbessert, deshalb vorsichtig: lieber ein Komma zu viel als
 * eine veränderte Zahl.
 *
 *   „A — B — C.“          → „A, B, C.“
 *   „Licht —, insbesondere“ → „Licht, insbesondere“
 *   „5–7“, „08:00 – 17:00“ → „5-7“, „08:00-17:00“ (zwischen Ziffern ein
 *                             Bindestrich: so bleiben auch Rufnummern heil)
 *   „Nord–Süd“            → „Nord-Süd“
 *   Zeilen „– Punkt“      → „• Punkt“ (nur in mehrzeiligem Text; ein
 *                             einzeiliger Text verliert den Strich vorn)
 *   „—“ allein            → „“
 *   „Gerüst - Aufbau“     → „Gerüst, Aufbau“ (nur zwischen Buchstaben; „0711 - 123“ bleibt)
 *
 * `\n` zählt auch als Zeilenwechsel, wenn er als Zeichenfolge im Text steht:
 * eine unlesbare Antwort kommt als JSON-Rohtext an.
 */
export const ohneGedankenstriche = (text: string): string => {
  if (!GEDANKENSTRICH.test(text) &&!/\p{L}[ \t]+-[ \t]+\p{L}/u.test(text)) return text;
  const zeilen = /\n|\\n/.test(text);
  let t = text
    // Aufzählung am Zeilenanfang
    .replace(/(^|\n|\\n)([ \t]*)[–—][ \t]*/g, (_m, vor: string, einzug: string) =>
      (zeilen ? `${vor}${einzug}• ` : `${vor}${einzug}`))
    // Zwischen Ziffern: Bindestrich ohne Leerzeichen
    .replace(/(\d\.?)[ \t]*[–—][ \t]*(?=\d)/g, '$1-')
    // Vor Komma oder Satzzeichen fällt der Strich weg
    .replace(/[ \t]*[–—][ \t]*(?=[,.;:!?)])/g, '')
    // Am Zeilenende fällt er weg
    .replace(/[ \t]*[–—][ \t]*(?=\n|\\n|$)/g, '')
    // Einschub und Nachsatz: Komma
    .replace(/(\S)[ \t]+[–—][ \t]+/g, '$1, ')
    .replace(/(\p{L})[ \t]+-[ \t]+(?=\p{L})/gu, '$1, ')
    // Zwischen Wörtern ohne Leerzeichen: Bindestrich
    .replace(/([\p{L}\p{N}])[–—](?=[\p{L}\p{N}])/gu, '$1-')
    // Nach einer öffnenden Klammer
    .replace(/\([ \t]*[–—][ \t]*/g, '(')
    // Was übrig ist
    .replace(/[ \t]*[–—][ \t]*/g, ' ');
  t = t
    .replace(/([,.;:!?]),/g, '$1')
    .replace(/\(, /g, '(')
    .replace(/(\S) {2,}(?=\S)/g, '$1 ');
  return t;
};

/** Die Rückfallform des Proxys für unlesbares JSON: nur `text` (und ggf. die Abschneide-Marke). */
const istRohtextAntwort = (v: unknown): boolean =>
  !!v && typeof v === 'object' && !Array.isArray(v)
  && typeof (v as Record<string, unknown>).text === 'string'
  && Object.keys(v as Record<string, unknown>).every((k) => k === 'text' || k === '_truncated');

/**
 * Alle Texte einer KI-Antwort ohne Gedankenstriche; Werte unter den Schlüsseln
 * `woertlich` bleiben, wie sie sind. Form und Reihenfolge der Antwort bleiben
 * gleich (die Pfade aus `x-regelwerke-verworfen` zeigen weiter richtig).
 */
export const bereinigeKiAntwort = <T>(wert: T, woertlich: readonly string[] = []): T => {
  // Unlesbare Antwort ({ text: Rohtext }): darin stehen die wörtlichen Felder
  // als Teil des JSON-Rohtexts, eine Ausnahme nach Schlüssel greift dort
  // nicht. Dann bleibt die Antwort, wie sie ist.
  if (woertlich.length && istRohtextAntwort(wert)) return wert;
  const lauf =(v: unknown, schluessel: string | null): unknown => {
    if (typeof v === 'string') return schluessel !== null && woertlich.includes(schluessel) ? v : ohneGedankenstriche(v);
    if (Array.isArray(v)) return v.map((e) => lauf(e, schluessel));
    if (v && typeof v === 'object') {
      const aus: Record<string, unknown> = {};
      for (const [k, e] of Object.entries(v as Record<string, unknown>)) aus[k] = lauf(e, k);
      return aus;
    }
    return v;
  };
  return lauf(wert, null) as T;
};
